import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, ExternalLink, Mail, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const lineV = { hidden: { y: "112%" }, visible: { y: 0 } };
const view = { once: true, amount: 0.2 };

const MaskedHeadLine = ({ children, delay = 0 }) => (
  <motion.span
    className="block overflow-hidden"
    initial="hidden"
    whileInView="visible"
    viewport={view}
  >
    <motion.span
      className="block"
      variants={lineV}
      transition={{ duration: 0.95, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </motion.span>
);

export default function Contact() {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-white/10 px-6 pt-24 pb-14 lg:px-12 lg:pt-36 lg:pb-16"
    >
      <h2 className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
        <MaskedHeadLine>Let&rsquo;s Get</MaskedHeadLine>
        <MaskedHeadLine delay={0.1}>
          To Work<span className="text-vermilion">.</span>
        </MaskedHeadLine>
      </h2>

      <div className="mt-14 lg:mt-20">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Prefer Email?
        </p>
        <button
          data-testid="contact-email-link"
          onClick={() => setChooserOpen(true)}
          className="break-all text-left font-display text-2xl font-bold tracking-tight text-white underline decoration-vermilion decoration-2 underline-offset-8 transition-colors duration-300 hover:text-vermilion active:opacity-70 md:text-5xl"
        >
          info@pixmarketing.uk
        </button>
      </div>

      {chooserOpen && (
        <div
          data-testid="email-chooser-backdrop"
          onClick={() => setChooserOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm"
        >
          <div
            data-testid="email-chooser-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-panel p-6 sm:p-8"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion">Choose your email</p>
                <p className="mt-2 font-display text-xl font-extrabold tracking-tight">Write to info@pixmarketing.uk</p>
              </div>
              <button
                data-testid="email-chooser-close"
                onClick={() => setChooserOpen(false)}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-neutral-400 transition-colors hover:border-vermilion hover:bg-vermilion hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6 space-y-3">
              {[
                {
                  id: "chooser-gmail",
                  label: "Gmail",
                  hint: "Opens in a new tab",
                  href: "https://mail.google.com/mail/?view=cm&fs=1&to=info@pixmarketing.uk&su=New%20project%20enquiry",
                  badge: "G",
                },
                {
                  id: "chooser-outlook",
                  label: "Outlook",
                  hint: "Opens in a new tab",
                  href: "https://outlook.office.com/mail/deeplink/compose?to=info@pixmarketing.uk&subject=New%20project%20enquiry",
                  badge: "O",
                },
              ].map((opt) => (
                <a
                  key={opt.id}
                  data-testid={`email-${opt.id}`}
                  href={opt.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setChooserOpen(false)}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 px-5 py-4 transition-colors hover:border-vermilion hover:bg-vermilion/5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white font-display text-base font-extrabold text-ink">
                    {opt.badge}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">{opt.label}</span>
                    <span className="block text-xs text-neutral-500">{opt.hint}</span>
                  </span>
                  <ExternalLink className="ml-auto h-4 w-4 text-neutral-500" />
                </a>
              ))}
              <a
                data-testid="email-chooser-mailapp"
                href="mailto:info@pixmarketing.uk"
                className="flex items-center gap-4 rounded-2xl border border-white/10 px-5 py-4 transition-colors hover:border-vermilion hover:bg-vermilion/5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <Mail className="h-4.5 w-4.5" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">Your mail app</span>
                  <span className="block text-xs text-neutral-500">Outlook app, Apple Mail, etc.</span>
                </span>
                <Mail className="ml-auto h-4 w-4 text-neutral-500" />
              </a>
              <button
                data-testid="email-chooser-copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText("info@pixmarketing.uk");
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                      setChooserOpen(false);
                    }, 1200);
                  } catch {}
                }}
                className="flex w-full items-center gap-4 rounded-2xl border border-white/10 px-5 py-4 text-left transition-colors hover:border-vermilion hover:bg-vermilion/5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  {copied ? <Check className="h-4.5 w-4.5 text-vermilion" /> : <Copy className="h-4.5 w-4.5" />}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{copied ? "Copied" : "Copy address"}</span>
                  <span className="block text-xs text-neutral-500">info@pixmarketing.uk</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
